import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fingerprint, MonitorSmartphone, ScrollText, ShieldAlert, Timer, Users } from "lucide-react";
import { ShellLayout } from "@/components/shell/ShellLayout";
import { StatCard } from "@/components/shell/StatCard";
import { FixtureBadge, TypeBadge } from "@/components/shell/Badges";
import { hasCapability, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatDateTime, timeUntil } from "@/lib/format";

export default function HubHome() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  useEffect(() => {
    api.get("/hub/overview").then(({ data }) => setOverview(data)).catch(() => setOverview(null));
  }, []);
  const security = overview?.security;
  const platform = overview?.platform;
  const canSeePlatform = hasCapability(user, "management.stats.read");

  return (
    <ShellLayout area="hub" eyebrow="Staff hub" title={`${user.display_name}`}>
      <div data-testid="hub-dashboard" className="space-y-8">
        <div className="flex flex-wrap items-center gap-3">
          <TypeBadge user={user} />
          <FixtureBadge user={user} />
          <p className="text-sm text-slate-400">Internal console. CRM and operational tooling arrive in Phase 1+.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard index={0} label="Authenticator" icon={Fingerprint} value={security ? (security.mfa_enabled ? "Enforced" : "Missing") : "…"} tone="good" sub="Required for staff" testId="hub-stat-mfa" />
          <StatCard index={1} label="Active sessions" icon={MonitorSmartphone} value={security ? security.active_sessions : "…"} sub="Your devices" testId="hub-stat-sessions" />
          <StatCard index={2} label="Session expires" icon={Timer} value={security ? timeUntil(security.session_expires_at) : "…"} tone="accent" sub="12-hour absolute lifetime" testId="hub-stat-expiry" />
          <StatCard index={3} label="Password changed" icon={ShieldAlert} value={security ? formatDateTime(security.password_changed_at).split(",")[0] : "…"} sub="No forced rotation policy" testId="hub-stat-password" />
        </div>
        {canSeePlatform ? (
          <section>
            <div className="mb-4 flex items-center gap-3">
              <h2 className="font-display text-base font-semibold text-white md:text-lg">Network posture</h2>
              <span className="bz-badge border-purple-500/30 bg-purple-500/15 text-purple-200">Management view</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <StatCard index={0} label="Identities" icon={Users} value={platform ? platform.users_total : "…"} sub={platform ? `${platform.staff_total} staff · ${platform.customers_total} customers` : ""} testId="hub-platform-users" />
              <StatCard index={1} label="Live sessions" icon={MonitorSmartphone} value={platform ? platform.active_sessions_total : "…"} sub="Platform-wide" testId="hub-platform-sessions" />
              <StatCard index={2} label="Audit events" icon={ScrollText} value={platform ? platform.audit_events_total : "…"} sub={platform ? `${platform.disabled_total} disabled account(s)` : ""} testId="hub-platform-audit" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {hasCapability(user, "management.users.read") ? (
                <Link to="/hub/people" data-testid="hub-go-people-link" className="bz-btn-secondary text-xs">
                  Manage people
                </Link>
              ) : null}
              {hasCapability(user, "audit.read") ? (
                <Link to="/hub/audit" data-testid="hub-go-audit-link" className="bz-btn-secondary text-xs">
                  Open audit ledger
                </Link>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </ShellLayout>
  );
}
